// Shared by the public website and editor. Never pass user-entered text or action payloads here.
export const MEASUREMENT_ID = 'G-7JS2E96ZW0';
const pages = new Set(['/', '/gallery', '/gallery/pattern', '/tutorials', '/privacy', '/terms-and-conditions', '/404.html', '/app', '/app/login', '/app/signup', '/app/profile', '/app/register', '/app/reset_password', '/app/editor', '/app/create-pattern', '/app/logout']);
let active = false;
let surface = 'website';
let lastPage = null;
let previousPage = '';

export function pagePath(value) {
  try {
    const path = new URL(value, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    // Dynamic segments can contain email addresses, activation tokens or private pattern IDs.
    const route = path.replace(/^(\/app\/(?:login|register|reset_password|editor|create-pattern))\/.*$/, '$1');
    return pages.has(route) ? route : '/404.html';
  } catch { return '/404.html'; }
}

function safeUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    return url.origin + (url.origin === window.location.origin ? pagePath(value) : '/');
  } catch { return ''; }
}

function send(...args) {
  // Analytics must never prevent an edit, sign-in or download (including with blockers installed).
  try { if (active) window.gtag(...args); } catch { /* Best effort only. */ }
}

export function initAnalytics(appSurface, { enabled = import.meta.env.MODE === 'production' && ['patternade.com', 'www.patternade.com'].includes(window.location.hostname) } = {}) {
  surface = appSurface;
  if (active || !enabled) return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    active = true;
    previousPage = safeUrl(document.referrer);
    send('js', new Date());
    send('config', MEASUREMENT_ID, {
      send_page_view: false,
      page_location: safeUrl(window.location.href),
      page_referrer: previousPage,
      page_title: pagePath(window.location.href),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.append(script);
  } catch { active = false; }
}

// Only fixed labels and enumerated metadata are accepted; no arbitrary text, URLs or identifiers.
const values = {
  destination: [...pages],
  method: ['email'],
  format: ['plt', 'pdf', 'json', 'dxf', 'svg', 'png', 'jpg'],
  paper_size: ['A5', 'A4', 'LETTER', 'A3', 'TABLOID', 'LEGAL', 'CUSTOM'],
  source: ['header', 'footer', 'hero', 'dialog', 'content', 'blank', 'base', 'measurements', 'profile'],
  reason: ['validation', 'rejected', 'network', 'sign_in_required'],
  tool: ['selection', 'draw', 'line', 'curve', 'knot', 'notch', 'dart', 'text', 'ruler', 'zoom', 'pan', 'guide', 'drillHole', 'internalLine', 'walk'],
};

export function trackEvent(name, parameters = {}) {
  if (!active || !/^[a-z][a-z0-9_]{0,39}$/.test(name)) return;
  const safe = {};
  Object.entries(values).forEach(([key, allowed]) => {
    if (allowed.includes(parameters[key])) safe[key] = parameters[key];
  });
  send('event', name, {
    ...safe, app_surface: surface,
    page_location: safeUrl(window.location.href),
    page_title: pagePath(window.location.href),
    page_referrer: previousPage,
    transport_type: 'beacon',
  });
}

export function trackPage(value = window.location.href) {
  // Ignore query/hash-only updates, but count opening a different pattern as another page view.
  const key = new URL(value, window.location.origin).pathname;
  if (!active || key === lastPage) return;
  lastPage = key;
  trackEvent('page_view');
  previousPage = safeUrl(value);
}
